import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import logo from "@/assets/logo.png";
import logoDark from "@/assets/logo-dark.png";
import PageFooter from "@/components/PageFooter";

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gradient-subtle flex flex-col">
      {/* Header */}
      <header className="glass-card border-b border-border/30 sticky top-0 z-10 shadow-elegant">
        <div className="container mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img src={logoDark} alt="untabbed.to logo" className="h-8 w-8 dark:hidden" />
              <img src={logo} alt="untabbed.to logo" className="h-8 w-8 hidden dark:block" />
              <h1 className="text-3xl font-display font-bold text-black dark:text-white tracking-tight">
                untabbed.to
              </h1>
            </Link>
            <Link to="/">
              <Button variant="outline" size="sm">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-12 flex-1 max-w-4xl">
        <div className="glass-card shadow-card p-8 rounded-lg">
          <h2 className="text-3xl font-display font-bold text-foreground mb-6">Privacy Policy</h2>
          <p className="text-sm text-muted-foreground mb-6">Last updated: November 11, 2025</p>
          
          <div className="space-y-6 text-foreground">
            <section>
              <h3 className="text-xl font-semibold mb-3">1. Introduction</h3>
              <p className="text-muted-foreground leading-relaxed">
                This Privacy Policy describes how Untabbed.to ("we", "us", or "our") collects, uses, and shares information
                when you use our Chrome browser extension and website. We are committed to protecting your privacy and complying
                with applicable data protection laws, including GDPR.
              </p>
            </section>

            <section>
              <h3 className="text-xl font-semibold mb-3">2. Data Controller</h3>
              <p className="text-muted-foreground leading-relaxed">
                The data controller responsible for your personal data is:<br />
                <strong>opendeluxe UG</strong><br />
                Teltower Damm 159<br />
                14167 Berlin, Germany<br />
                Email: <a href="mailto:contact@opendeluxe.com" className="text-primary hover:text-primary-glow transition-colors">contact@opendeluxe.com</a><br />
                Phone: <a href="tel:+4915678521554" className="text-primary hover:text-primary-glow transition-colors">+49(0)15678-521554</a>
              </p>
            </section>

            <section>
              <h3 className="text-xl font-semibold mb-3">3. Chrome Extension Data Collection</h3>

              <h4 className="text-lg font-medium mb-2 mt-4 text-foreground">3.1 What Data We Collect</h4>
              <p className="text-muted-foreground leading-relaxed mb-3">
                When you use the Untabbed.to Chrome extension, the following data is captured:
              </p>
              <ul className="list-disc list-inside text-muted-foreground leading-relaxed space-y-2">
                <li><strong>Tab URLs:</strong> The web addresses of your open browser tabs</li>
                <li><strong>Page Titles:</strong> The titles of your open tabs</li>
                <li><strong>Favicons:</strong> Website icons associated with your tabs</li>
                <li><strong>Page Metadata:</strong> Thumbnails, descriptions, and Open Graph images when available</li>
                <li><strong>Collection Titles:</strong> Custom titles you assign to your tab collections</li>
                <li><strong>Timestamps:</strong> When you captured your tab collection</li>
                <li><strong>Window Information:</strong> Which browser window the tabs belong to</li>
              </ul>

              <h4 className="text-lg font-medium mb-2 mt-4 text-foreground">3.2 Why We Need Browser Permissions</h4>
              <ul className="list-disc list-inside text-muted-foreground leading-relaxed space-y-2">
                <li><strong>tabs:</strong> Required to read tab URLs and titles when you capture a collection</li>
                <li><strong>activeTab:</strong> Required to identify which tab is currently active</li>
                <li><strong>storage:</strong> Required to store tab collections and extension settings locally in your browser</li>
                <li><strong>scripting:</strong> Required to interact with web pages for capturing metadata (thumbnails, descriptions)</li>
                <li><strong>all_urls:</strong> Required to capture tabs from any website you visit</li>
              </ul>

              <h4 className="text-lg font-medium mb-2 mt-4 text-foreground">3.3 How We Use This Data</h4>
              <p className="text-muted-foreground leading-relaxed">
                This data is used to:
              </p>
              <ul className="list-disc list-inside text-muted-foreground leading-relaxed space-y-2">
                <li>Display your captured tab collections in the local extension viewer</li>
                <li>Generate visual previews with thumbnails and descriptions</li>
                <li>Allow you to reopen saved tabs later</li>
                <li>Provide export functionality to browser bookmarks</li>
                <li>Enable optional cloud sharing when you explicitly choose to share a collection</li>
              </ul>
            </section>

            <section>
              <h3 className="text-xl font-semibold mb-3">4. Data Storage and Security</h3>

              <h4 className="text-lg font-medium mb-2 mt-4 text-foreground">4.1 Extension Registration</h4>
              <p className="text-muted-foreground leading-relaxed mb-3">
                When you first install the extension, it performs a <strong>one-time registration</strong> with our server:
              </p>
              <ul className="list-disc list-inside text-muted-foreground leading-relaxed space-y-2">
                <li><strong>Installation ID:</strong> A randomly generated UUID created locally on your device</li>
                <li><strong>Registration API Call:</strong> Sends the installation ID and browser User-Agent header to receive an authentication token</li>
                <li><strong>User-Agent Data:</strong> We collect your browser's User-Agent string (e.g., "Chrome/120.0.0.0") for debugging and support purposes</li>
                <li><strong>Purpose:</strong> This token authenticates your extension when you choose to share collections</li>
                <li><strong>No personal data:</strong> We do not collect name, email, IP address, or browsing history during registration</li>
                <li><strong>One token per installation:</strong> Ensures security and allows token revocation if needed</li>
              </ul>

              <h4 className="text-lg font-medium mb-2 mt-4 text-foreground">4.2 Local Storage (Default)</h4>
              <p className="text-muted-foreground leading-relaxed mb-3">
                After registration, all captured tab collections are stored <strong>exclusively in your browser's local storage</strong>:
              </p>
              <ul className="list-disc list-inside text-muted-foreground leading-relaxed space-y-2">
                <li><strong>Chrome Extension Storage API:</strong> All tab collection data remains on your device</li>
                <li><strong>No automatic cloud sync:</strong> Your tab data never leaves your browser unless you explicitly share it</li>
                <li><strong>Full local control:</strong> You can view, edit, export, and delete collections entirely offline</li>
                <li><strong>Privacy by design:</strong> We cannot access your locally stored collections</li>
              </ul>

              <h4 className="text-lg font-medium mb-2 mt-4 text-foreground">4.3 Optional Cloud Sharing</h4>
              <p className="text-muted-foreground leading-relaxed mb-3">
                When you explicitly choose to share a collection using the "Share" feature, the collection data is uploaded to:
              </p>
              <ul className="list-disc list-inside text-muted-foreground leading-relaxed space-y-2">
                <li><strong>Supabase (PostgreSQL):</strong> Our secure database hosted in EU data centers (GDPR compliant)</li>
                <li><strong>Vercel:</strong> Our web hosting and API provider (GDPR compliant)</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mt-3">
                <strong>Important: Shared collections are publicly accessible.</strong> When you share a collection, it is assigned a unique,
                unguessable URL. <strong>Anyone who has this URL can access the collection from anywhere in the world, at any time,
                without authentication.</strong> The collection becomes publicly viewable (though not indexed by search engines).
                Only share collection URLs with people you trust, and never share collections containing sensitive, confidential,
                or private information.
              </p>

              <h4 className="text-lg font-medium mb-2 mt-4 text-foreground">4.4 Data Retention</h4>
              <ul className="list-disc list-inside text-muted-foreground leading-relaxed space-y-2">
                <li><strong>Local Collections:</strong> Stored indefinitely in your browser until you manually delete them or clear browser data</li>
                <li><strong>Shared Collections:</strong> Stored on our servers indefinitely unless you request deletion</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mt-3">
                To request deletion of a shared collection, contact us at <a href="mailto:contact@opendeluxe.com" className="text-primary hover:text-primary-glow transition-colors">contact@opendeluxe.com</a> with the collection URL.
              </p>

              <h4 className="text-lg font-medium mb-2 mt-4 text-foreground">4.5 Shared URL Security Model</h4>
              <p className="text-muted-foreground leading-relaxed mb-3">
                Shared collection URLs use the format: <code className="text-primary">https://untabbed.to/collection/[COLLECTION_ID]</code>
              </p>
              <ul className="list-disc list-inside text-muted-foreground leading-relaxed space-y-2">
                <li><strong>ID Format:</strong> Collection IDs are generated using nanoid with 21 characters from a URL-safe alphabet (A-Za-z0-9_-)</li>
                <li><strong>Entropy:</strong> 126 bits of entropy (~2^126 possible IDs), making IDs computationally infeasible to guess</li>
                <li><strong>Collision Resistance:</strong> Probability of collision is negligible (comparable to UUID v4)</li>
                <li><strong>Security Through Obscurity:</strong> While URLs are unguessable, they provide security through obscurity only. Anyone with the URL has full access.</li>
                <li><strong>No Expiration:</strong> Shared URLs remain valid indefinitely unless you request deletion</li>
                <li><strong>Search Engine Protection:</strong> Collections include noindex/nofollow directives to prevent search engine indexing</li>
              </ul>

              <h4 className="text-lg font-medium mb-2 mt-4 text-foreground">4.6 Additional Security Measures</h4>
              <ul className="list-disc list-inside text-muted-foreground leading-relaxed space-y-2">
                <li>All data transmission uses HTTPS/TLS encryption</li>
                <li>API rate limiting prevents abuse (max 200 tabs per collection)</li>
                <li>Input validation and URL length restrictions prevent malicious data injection</li>
                <li>Per-installation authentication tokens (64-character hex) with expiration and revocation support</li>
                <li>Regular security audits and updates</li>
              </ul>
            </section>

            <section>
              <h3 className="text-xl font-semibold mb-3">5. Data Sharing and Third Parties</h3>
              <p className="text-muted-foreground leading-relaxed mb-3">
                <strong>We do not sell, trade, or rent your personal data.</strong> Your locally stored collections are never shared with us or any third party.
              </p>

              <h4 className="text-lg font-medium mb-2 mt-4 text-foreground">5.1 User-Initiated Sharing</h4>
              <p className="text-muted-foreground leading-relaxed mb-3">
                When you explicitly choose to share a collection using the "Share" button, the collection data is uploaded to our cloud service and:
              </p>
              <ul className="list-disc list-inside text-muted-foreground leading-relaxed space-y-2">
                <li>A unique, shareable URL is generated</li>
                <li><strong>Anyone with this URL can view the collection from anywhere in the world (no authentication required)</strong></li>
                <li>The collection becomes publicly accessible via that URL, though not indexed by search engines</li>
                <li>The URL remains valid indefinitely unless you request deletion</li>
                <li><strong>You control what gets shared</strong> - we only upload what you explicitly choose to share</li>
                <li><strong>Warning:</strong> Do not share collections containing sensitive, confidential, or private information</li>
              </ul>

              <h4 className="text-lg font-medium mb-2 mt-4 text-foreground">5.2 Service Providers</h4>
              <p className="text-muted-foreground leading-relaxed mb-3">
                For shared collections only, we use the following GDPR-compliant service providers:
              </p>
              <ul className="list-disc list-inside text-muted-foreground leading-relaxed space-y-2">
                <li><strong>Supabase:</strong> Database hosting (EU servers, GDPR compliant)</li>
                <li><strong>Vercel:</strong> Web hosting and API infrastructure (GDPR compliant)</li>
              </ul>

              <h4 className="text-lg font-medium mb-2 mt-4 text-foreground">5.3 Legal Requirements</h4>
              <p className="text-muted-foreground leading-relaxed">
                We may disclose shared collection data when required by law or to protect our legal rights.
              </p>
            </section>

            <section>
              <h3 className="text-xl font-semibold mb-3">6. Your Rights (GDPR)</h3>
              <p className="text-muted-foreground leading-relaxed mb-3">
                Under GDPR, you have the following rights:
              </p>
              <ul className="list-disc list-inside text-muted-foreground leading-relaxed space-y-2">
                <li><strong>Right to Access:</strong> Request a copy of your stored data</li>
                <li><strong>Right to Rectification:</strong> Request correction of inaccurate data</li>
                <li><strong>Right to Erasure:</strong> Request deletion of your data</li>
                <li><strong>Right to Restriction:</strong> Request limitation of processing</li>
                <li><strong>Right to Data Portability:</strong> Receive your data in a structured format</li>
                <li><strong>Right to Object:</strong> Object to processing of your data</li>
                <li><strong>Right to Withdraw Consent:</strong> Withdraw consent at any time</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mt-3">
                To exercise any of these rights, contact us at <a href="mailto:contact@opendeluxe.com" className="text-primary hover:text-primary-glow transition-colors">contact@opendeluxe.com</a>.
              </p>
            </section>

            <section>
              <h3 className="text-xl font-semibold mb-3">7. Cookies and Browser Storage</h3>
              <p className="text-muted-foreground leading-relaxed mb-3">
                Our website and extension use minimal browser storage for:
              </p>
              <ul className="list-disc list-inside text-muted-foreground leading-relaxed space-y-2">
                <li><strong>Theme Preferences:</strong> Light/dark mode settings (localStorage)</li>
                <li><strong>Extension Storage:</strong> Tab collections stored locally in Chrome's storage API</li>
                <li><strong>Extension Settings:</strong> User preferences for the extension</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mt-3">
                <strong>We do not use:</strong>
              </p>
              <ul className="list-disc list-inside text-muted-foreground leading-relaxed space-y-2">
                <li>Tracking cookies</li>
                <li>Analytics tools (Google Analytics, etc.)</li>
                <li>Third-party advertising or tracking scripts</li>
                <li>External font providers (all fonts are self-hosted for GDPR compliance)</li>
              </ul>
            </section>

            <section>
              <h3 className="text-xl font-semibold mb-3">8. Third-Party Services</h3>
              <p className="text-muted-foreground leading-relaxed mb-3">
                We use the following third-party services:
              </p>
              <ul className="list-disc list-inside text-muted-foreground leading-relaxed space-y-2">
                <li><strong>Supabase:</strong> Database hosting (EU servers, GDPR compliant)</li>
                <li><strong>Vercel:</strong> Website and API hosting (GDPR compliant)</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mt-3">
                We do not use Google Analytics, Facebook Pixel, or any other third-party tracking services.
              </p>
            </section>

            <section>
              <h3 className="text-xl font-semibold mb-3">9. Children's Privacy</h3>
              <p className="text-muted-foreground leading-relaxed">
                Our service is not directed to children under 16. We do not knowingly collect personal data
                from children under 16. If you believe we have collected data from a child, please contact us
                immediately.
              </p>
            </section>

            <section>
              <h3 className="text-xl font-semibold mb-3">10. Changes to This Policy</h3>
              <p className="text-muted-foreground leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any changes by
                updating the "Last updated" date at the top of this policy. Continued use of the service
                after changes constitutes acceptance of the updated policy.
              </p>
            </section>

            <section>
              <h3 className="text-xl font-semibold mb-3">11. Contact & Complaints</h3>
              <p className="text-muted-foreground leading-relaxed">
                For privacy-related questions or to exercise your rights, contact us at:<br />
                Email: <a href="mailto:contact@opendeluxe.com" className="text-primary hover:text-primary-glow transition-colors">contact@opendeluxe.com</a><br />
                Phone: <a href="tel:+4915678521554" className="text-primary hover:text-primary-glow transition-colors">+49(0)15678-521554</a>
              </p>
              <p className="text-muted-foreground leading-relaxed mt-3">
                You also have the right to lodge a complaint with a supervisory authority, particularly in the
                EU member state of your residence, place of work, or place of alleged infringement.
              </p>
            </section>
          </div>
        </div>
      </main>

      {/* Footer */}
      <PageFooter />
    </div>
  );
};

export default PrivacyPolicy;
